import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useClerk, useAuth } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Slider } from "../components/ui/slider";
import { AlertCircle, Info } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { cn } from "../lib/utils";

interface WidgetSettings {
  businessName: string;
  primaryColor: string;
  accentColor: string;
  fontFamily: string;
  buttonStyle: "rounded" | "square" | "pill";
  showLogo: boolean;
  logoUrl: string;
  allowDeposits: boolean;
  depositAmount: number;
  maxPartySize: number;
  timeSlotInterval: 15 | 30 | 60;
  customCss: string;
  embedCode: string;
}

export default function WidgetConfigurator() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { session } = useClerk();
  const { isSignedIn, isLoaded } = useAuth();

  const [settings, setSettings] = useState<WidgetSettings>({
    businessName: "",
    primaryColor: "#3b82f6",
    accentColor: "#10b981",
    fontFamily: "Inter",
    buttonStyle: "rounded",
    showLogo: true,
    logoUrl: "",
    allowDeposits: false,
    depositAmount: 10,
    maxPartySize: 10,
    timeSlotInterval: 30,
    customCss: "",
    embedCode: "",
  });

  const [iframeUrl, setIframeUrl] = useState<string>("");
  const [activeTab, setActiveTab] = useState<string>("appearance");
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");

  // Function to get auth headers
  const getAuthHeaders = async () => {
    if (!session) {
      console.error("No active session");
      return {};
    }

    const token = await session.getToken();
    return {
      Authorization: `Bearer ${token}`,
    };
  };

  const { data: businessData } = useQuery({
    queryKey: ["business"],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await axios.get("/api/businesses/my", { headers });
      return response.data;
    },
    enabled: !!isSignedIn && !!session,
  });

  const { data: subscriptionData } = useQuery({
    queryKey: ["subscription"],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await axios.get("/api/subscriptions/current", {
        headers,
      });
      return response.data;
    },
    enabled: !!isSignedIn && !!session,
  });

  // Check if the user has access to this feature based on their subscription
  const hasCustomBranding =
    subscriptionData?.subscription?.plan === "GROWTH" ||
    subscriptionData?.subscription?.plan === "PREMIUM";

  useEffect(() => {
    if (businessData) {
      setSettings((prevSettings) => ({
        ...prevSettings,
        businessName: businessData.name || "",
        logoUrl: businessData.logoUrl || "",
      }));
    }
  }, [businessData]);

  useEffect(() => {
    // Generate the embed code
    const widgetId = businessData?.id || "demo";
    const embedCode = `<script src="https://tablemint.com/widget/${widgetId}.js"></script>
<div id="tablemint-booking-widget" data-color="${settings.primaryColor}" data-accent="${settings.accentColor}" data-style="${settings.buttonStyle}"></div>`;

    setSettings((prev) => ({ ...prev, embedCode }));

    // Generate the preview URL
    setIframeUrl(
      `https://tablemint.com/widget/preview?businessId=${widgetId}&primaryColor=${encodeURIComponent(settings.primaryColor)}&accentColor=${encodeURIComponent(settings.accentColor)}&buttonStyle=${settings.buttonStyle}&fontFamily=${settings.fontFamily}&showLogo=${settings.showLogo}&logoUrl=${encodeURIComponent(settings.logoUrl)}`
    );
  }, [
    settings.primaryColor,
    settings.accentColor,
    settings.buttonStyle,
    settings.fontFamily,
    settings.showLogo,
    settings.logoUrl,
    businessData,
  ]);

  const handleSaveSettings = async () => {
    setSaveStatus("saving");
    try {
      const headers = await getAuthHeaders();

      // Create a payload that matches the SaveWidgetSettingsDto schema
      const validPayload = {
        businessName: settings.businessName,
        primaryColor: settings.primaryColor,
        accentColor: settings.accentColor,
        fontFamily: settings.fontFamily,
        buttonStyle: settings.buttonStyle,
        showLogo: settings.showLogo,
        logoUrl: settings.logoUrl,
        allowDeposits: settings.allowDeposits,
        depositAmount: settings.depositAmount,
        maxPartySize: settings.maxPartySize,
        timeSlotInterval: settings.timeSlotInterval,
        customCss: settings.customCss,
        // Omit embedCode as it's not part of the DTO
      };

      await axios.post("/api/businesses/widget-settings", validPayload, {
        headers,
      });

      setSaveStatus("saved");
      toast.success("Widget settings saved successfully");
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch (error) {
      console.error("Error saving widget settings", error);
      setSaveStatus("error");
      toast.error("Failed to save widget settings");
    }
  };

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(settings.embedCode);
    // Show a toast or notification
  };

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>{t("common.loading")}</p>
      </div>
    );
  }

  if (!isSignedIn) {
    navigate("/login");
    return null;
  }

  return (
    <div className="container mx-auto py-10">
      <div className="flex flex-col md:flex-row gap-8">
        <div className="w-full md:w-1/2">
          <h1 className="text-3xl font-bold mb-6">{t("configurator.title")}</h1>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="grid grid-cols-3 mb-6">
              <TabsTrigger value="appearance">
                {t("configurator.tabs.appearance")}
              </TabsTrigger>
              <TabsTrigger value="settings">
                {t("configurator.tabs.settings")}
              </TabsTrigger>
              <TabsTrigger value="embed">
                {t("configurator.tabs.embed")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="appearance" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t("configurator.branding")}</CardTitle>
                  <CardDescription>
                    {t("configurator.brandingDescription")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="businessName">
                      {t("configurator.businessName")}
                    </Label>
                    <Input
                      id="businessName"
                      value={settings.businessName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setSettings({
                          ...settings,
                          businessName: e.target.value,
                        })
                      }
                    />
                  </div>

                  {!hasCustomBranding && (
                    <Alert variant="default">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>
                        {t("configurator.upgradeRequired")}
                      </AlertTitle>
                      <AlertDescription>
                        {t("configurator.upgradeDescription")}
                        <Button
                          variant="link"
                          className="p-0 h-auto"
                          onClick={() => navigate("/pricing")}
                        >
                          {t("configurator.upgradePlan")}
                        </Button>
                      </AlertDescription>
                    </Alert>
                  )}

                  <div
                    className={cn(
                      !hasCustomBranding && "opacity-50 pointer-events-none"
                    )}
                  >
                    <Label htmlFor="primaryColor">
                      {t("configurator.primaryColor")}
                    </Label>
                    <div className="flex items-center gap-2">
                      <div
                        className="h-10 w-10 rounded-full border"
                        style={{ backgroundColor: settings.primaryColor }}
                      />
                      <Input
                        id="primaryColor"
                        type="text"
                        value={settings.primaryColor}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setSettings({
                            ...settings,
                            primaryColor: e.target.value,
                          })
                        }
                        className="font-mono"
                      />
                    </div>
                  </div>

                  <div
                    className={cn(
                      !hasCustomBranding && "opacity-50 pointer-events-none"
                    )}
                  >
                    <Label htmlFor="accentColor">
                      {t("configurator.accentColor")}
                    </Label>
                    <div className="flex items-center gap-2">
                      <div
                        className="h-10 w-10 rounded-full border"
                        style={{ backgroundColor: settings.accentColor }}
                      />
                      <Input
                        id="accentColor"
                        type="text"
                        value={settings.accentColor}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setSettings({
                            ...settings,
                            accentColor: e.target.value,
                          })
                        }
                        className="font-mono"
                      />
                    </div>
                  </div>

                  <div
                    className={cn(
                      !hasCustomBranding && "opacity-50 pointer-events-none"
                    )}
                  >
                    <Label htmlFor="fontFamily">
                      {t("configurator.fontFamily")}
                    </Label>
                    <Select
                      value={settings.fontFamily}
                      onValueChange={(value: string) =>
                        setSettings({ ...settings, fontFamily: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Inter">Inter</SelectItem>
                        <SelectItem value="Roboto">Roboto</SelectItem>
                        <SelectItem value="Lato">Lato</SelectItem>
                        <SelectItem value="Poppins">Poppins</SelectItem>
                        <SelectItem value="Open Sans">Open Sans</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div
                    className={cn(
                      !hasCustomBranding && "opacity-50 pointer-events-none"
                    )}
                  >
                    <Label htmlFor="buttonStyle">
                      {t("configurator.buttonStyle")}
                    </Label>
                    <Select
                      value={settings.buttonStyle}
                      onValueChange={(value: "rounded" | "square" | "pill") =>
                        setSettings({ ...settings, buttonStyle: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rounded">
                          {t("configurator.buttonStyles.rounded")}
                        </SelectItem>
                        <SelectItem value="square">
                          {t("configurator.buttonStyles.square")}
                        </SelectItem>
                        <SelectItem value="pill">
                          {t("configurator.buttonStyles.pill")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div
                    className={cn(
                      !hasCustomBranding && "opacity-50 pointer-events-none"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <Label htmlFor="showLogo">
                        {t("configurator.showLogo")}
                      </Label>
                      <Switch
                        id="showLogo"
                        checked={settings.showLogo}
                        onCheckedChange={(checked: boolean) =>
                          setSettings({ ...settings, showLogo: checked })
                        }
                      />
                    </div>
                  </div>

                  {settings.showLogo && (
                    <div
                      className={cn(
                        !hasCustomBranding && "opacity-50 pointer-events-none"
                      )}
                    >
                      <Label htmlFor="logoUrl">
                        {t("configurator.logoUrl")}
                      </Label>
                      <Input
                        id="logoUrl"
                        type="text"
                        value={settings.logoUrl}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setSettings({ ...settings, logoUrl: e.target.value })
                        }
                        placeholder="https://example.com/logo.png"
                      />
                      {settings.logoUrl && (
                        <div className="mt-2 p-2 border rounded flex items-center justify-center">
                          <img
                            src={settings.logoUrl}
                            alt="Logo Preview"
                            className="max-h-16"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                "https://placehold.co/200x80?text=Invalid+URL";
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="settings" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t("configurator.bookingSettings")}</CardTitle>
                  <CardDescription>
                    {t("configurator.bookingSettingsDescription")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="maxPartySize">
                      {t("configurator.maxPartySize")}
                    </Label>
                    <div className="flex items-center gap-4">
                      <Slider
                        id="maxPartySize"
                        value={[settings.maxPartySize]}
                        min={1}
                        max={20}
                        step={1}
                        onValueChange={(value: number[]) =>
                          setSettings({ ...settings, maxPartySize: value[0] })
                        }
                        className="flex-1"
                      />
                      <span className="w-10 text-center">
                        {settings.maxPartySize}
                      </span>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="timeSlotInterval">
                      {t("configurator.timeSlotInterval")}
                    </Label>
                    <Select
                      value={settings.timeSlotInterval.toString()}
                      onValueChange={(value: string) =>
                        setSettings({
                          ...settings,
                          timeSlotInterval: parseInt(value) as 15 | 30 | 60,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">
                          15 {t("configurator.minutes")}
                        </SelectItem>
                        <SelectItem value="30">
                          30 {t("configurator.minutes")}
                        </SelectItem>
                        <SelectItem value="60">
                          60 {t("configurator.minutes")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div
                    className={cn(
                      subscriptionData?.subscription?.plan !== "PREMIUM" &&
                        "opacity-50 pointer-events-none"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <Label htmlFor="allowDeposits">
                        {t("configurator.allowDeposits")}
                      </Label>
                      <Switch
                        id="allowDeposits"
                        checked={settings.allowDeposits}
                        onCheckedChange={(checked: boolean) =>
                          setSettings({ ...settings, allowDeposits: checked })
                        }
                        disabled={
                          subscriptionData?.subscription?.plan !== "PREMIUM"
                        }
                      />
                    </div>

                    {subscriptionData?.subscription?.plan !== "PREMIUM" && (
                      <p className="text-sm text-muted-foreground mt-1">
                        <Info className="h-4 w-4 inline mr-1" />
                        {t("configurator.premiumFeature")}
                      </p>
                    )}

                    {settings.allowDeposits && (
                      <div className="mt-2">
                        <Label htmlFor="depositAmount">
                          {t("configurator.depositAmount")}
                        </Label>
                        <div className="flex items-center">
                          <Input
                            id="depositAmount"
                            type="number"
                            min={1}
                            value={settings.depositAmount}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) =>
                              setSettings({
                                ...settings,
                                depositAmount: parseInt(e.target.value) || 0,
                              })
                            }
                            className="w-24"
                          />
                          <span className="ml-2">USD</span>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card
                className={cn(
                  !hasCustomBranding && "opacity-50 pointer-events-none"
                )}
              >
                <CardHeader>
                  <CardTitle>{t("configurator.advancedSettings")}</CardTitle>
                  <CardDescription>
                    {t("configurator.advancedSettingsDescription")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div>
                    <Label htmlFor="customCss">
                      {t("configurator.customCss")}
                    </Label>
                    <textarea
                      id="customCss"
                      value={settings.customCss}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setSettings({ ...settings, customCss: e.target.value })
                      }
                      rows={5}
                      className="w-full p-2 border rounded-md font-mono text-sm mt-1"
                      placeholder=".tablemint-widget { /* your custom CSS */ }"
                    />
                  </div>

                  {!hasCustomBranding && (
                    <p className="text-sm text-muted-foreground mt-2">
                      <Info className="h-4 w-4 inline mr-1" />
                      {t("configurator.upgradeForCustomization")}
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="embed" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t("configurator.embedWidget")}</CardTitle>
                  <CardDescription>
                    {t("configurator.embedDescription")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted p-4 rounded-md">
                    <pre className="text-sm font-mono whitespace-pre-wrap break-all">
                      {settings.embedCode}
                    </pre>
                  </div>
                  <Button className="mt-4" onClick={copyEmbedCode}>
                    {t("configurator.copyCode")}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t("configurator.instructions")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="list-decimal list-inside space-y-2">
                    <li>{t("configurator.step1")}</li>
                    <li>{t("configurator.step2")}</li>
                    <li>{t("configurator.step3")}</li>
                  </ol>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="mt-6 flex justify-end">
            <Button
              onClick={handleSaveSettings}
              disabled={saveStatus === "saving"}
            >
              {saveStatus === "saving"
                ? t("configurator.saving")
                : saveStatus === "saved"
                  ? t("configurator.saved")
                  : t("configurator.saveSettings")}
            </Button>
          </div>
        </div>

        <div className="w-full md:w-1/2">
          <div className="sticky top-24">
            <div className="border rounded-lg overflow-hidden bg-white mt-[7.5rem]">
              <div className="h-10 bg-gray-100 border-b flex items-center px-4">
                <div className="w-3 h-3 rounded-full bg-red-500 mr-2" />
                <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <div className="text-xs text-gray-600 mx-auto">
                  tablemint.com
                </div>
              </div>
              <div className="p-4 h-[600px] overflow-auto">
                {iframeUrl ? (
                  <iframe
                    src={iframeUrl}
                    className="w-full h-full border-0"
                    title="Widget Preview"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-400">
                      {t("configurator.previewLoading")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
